import React, { useEffect, useState } from 'react';
import { staffClient } from 'clients/staff';
import Button from 'ui/Button';
import Input from 'ui/Input';
import Modal from 'ui/Modal';
import Table, { Column } from 'ui/Table';
import Loading from 'ui/Loading';
import Section from 'ui/Section';
import { Switch } from 'ui/shadcn';
import ImageUploader, { ImageFile } from 'ui/ImageUploader';
import { getS3UploadUrl, uploadS3File } from 'utils/s3';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface CareerItem { year: string; desc: string; }
interface AwardItem { year: string; label: string; }

interface Executive {
    id: number;
    name: string;
    role: string;
    photo_url?: string;
    philosophy?: string;
    qualifications: string[];
    education: string[];
    career: CareerItem[];
    awards: AwardItem[];
    societies: string[];
    publications: string[];
    is_active: boolean;
    display_order: number;
}

const empty: Omit<Executive, 'id'> = {
    name: '', role: '', photo_url: '', philosophy: '',
    qualifications: [], education: [], career: [], awards: [],
    societies: [], publications: [],
    is_active: true, display_order: 0,
};

// ─────────────────────────────────────────────
// 외부 컴포넌트 — 부모 리렌더 시 재마운트 방지 (포커스/입력 손실 X)
// ─────────────────────────────────────────────

interface StringListProps {
    label: string;
    list: string[];
    onChange: (v: string[]) => void;
    placeholder: string;
}
const StringList: React.FC<StringListProps> = ({ label, list, onChange, placeholder }) => (
    <div className="form-group">
        <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-neutral-700">{label}</label>
            <Button variant="ghost" size="small" icon={<PlusIcon />} onClick={() => onChange([...list, ''])}>
                추가
            </Button>
        </div>
        <div className="space-y-1.5">
            {list.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                    <input
                        className="h-8 flex-1 rounded border border-input bg-background px-2 text-base text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                        value={s}
                        onChange={(e) => onChange(list.map((x, idx) => idx === i ? e.target.value : x))}
                        placeholder={placeholder}
                    />
                    <button
                        onClick={() => onChange(list.filter((_, idx) => idx !== i))}
                        className="rounded p-1.5 text-neutral-400 hover:bg-muted hover:text-destructive"
                    >
                        <TrashIcon className="w-4 h-4" />
                    </button>
                </div>
            ))}
            {list.length === 0 && <p className="text-xs text-neutral-400">없음</p>}
        </div>
    </div>
);

interface PairListProps<T extends Record<string, any>> {
    label: string;
    list: T[];
    descKey: keyof T;          // 'desc' (career) | 'label' (awards) 명시
    onChange: (v: T[]) => void;
    yearLabel?: string;
    descLabel?: string;
}
function PairList<T extends Record<string, any>>({ label, list, descKey, onChange, yearLabel = '연도', descLabel = '내용' }: PairListProps<T>) {
    return (
        <div className="form-group">
            <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-neutral-700">{label}</label>
                <Button
                    variant="ghost" size="small" icon={<PlusIcon />}
                    onClick={() => onChange([...list, { year: '', [descKey]: '' } as unknown as T])}
                >
                    추가
                </Button>
            </div>
            <div className="space-y-1.5">
                {list.map((item, i) => (
                    <div key={i} className="grid grid-cols-[140px_1fr_auto] items-center gap-2">
                        <input
                            className="h-8 w-full rounded border border-input bg-background px-2 text-base text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                            value={item.year || ''}
                            onChange={(e) => onChange(list.map((x, idx) => idx === i ? { ...x, year: e.target.value } : x))}
                            placeholder={yearLabel}
                        />
                        <input
                            className="h-8 w-full min-w-0 rounded border border-input bg-background px-2 text-base text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                            value={item[descKey] || ''}
                            onChange={(e) => onChange(list.map((x, idx) => idx === i ? { ...x, [descKey]: e.target.value } : x))}
                            placeholder={descLabel}
                        />
                        <button
                            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
                            className="rounded p-1.5 text-neutral-400 hover:bg-muted hover:text-destructive"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                {list.length === 0 && <p className="text-xs text-neutral-400">없음</p>}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────
// 메인 매니저 컴포넌트
// ─────────────────────────────────────────────

const ExecutiveManager: React.FC = () => {
    const [list, setList] = useState<Executive[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<Executive | null>(null);
    const [form, setForm] = useState<Omit<Executive, 'id'>>({ ...empty });
    const [photoImages, setPhotoImages] = useState<ImageFile[]>([]);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { fetchList(); }, []);

    const fetchList = async () => {
        try {
            const res = await staffClient.get('/website-executive');
            setList(res.data);
        } catch {
            toast.error('경영진 정보를 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ ...empty });
        setPhotoImages([]);
        setShowModal(true);
    };

    const openEdit = (row: Executive) => {
        setEditing(row);
        setForm({
            name: row.name, role: row.role,
            photo_url: row.photo_url || '', philosophy: row.philosophy || '',
            qualifications: row.qualifications || [],
            education: row.education || [],
            career: row.career || [],
            awards: row.awards || [],
            societies: row.societies || [],
            publications: row.publications || [],
            is_active: row.is_active, display_order: row.display_order,
        });
        setPhotoImages(row.photo_url ? [{ id: '1', url: row.photo_url, name: 'photo.jpg' }] : []);
        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            setUploading(true);
            let photoUrl = form.photo_url || '';

            if (photoImages.length > 0 && photoImages[0].file) {
                const file = photoImages[0].file;
                const signedUrl = await getS3UploadUrl(file);
                if (signedUrl) {
                    const uploaded = await uploadS3File(file, signedUrl);
                    if (uploaded) photoUrl = uploaded;
                }
            } else if (photoImages.length === 0) {
                photoUrl = '';
            } else if (photoImages[0].url) {
                photoUrl = photoImages[0].url;
            }

            const payload = { ...form, photo_url: photoUrl };

            if (editing) {
                await staffClient.patch(`/website-executive/${editing.id}`, payload);
                toast.success('경영진 정보가 수정되었습니다.');
            } else {
                await staffClient.post('/website-executive', payload);
                toast.success('경영진이 추가되었습니다.');
            }
            setShowModal(false);
            fetchList();
        } catch (error) {
            console.error('Executive save failed:', error);
            toast.error('저장에 실패했습니다.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (row: Executive) => {
        if (!window.confirm(`"${row.name}" 정보를 삭제하시겠습니까?`)) return;
        try {
            await staffClient.delete(`/website-executive/${row.id}`);
            toast.success('삭제되었습니다.');
            fetchList();
        } catch {
            toast.error('삭제에 실패했습니다.');
        }
    };

    const handleToggle = async (row: Executive) => {
        try {
            const next = !row.is_active;
            await staffClient.patch(`/website-executive/${row.id}`, { is_active: next });
            setList(prev => prev.map(r => r.id === row.id ? { ...r, is_active: next } : r));
            toast.success(next ? '활성화되었습니다.' : '비활성화되었습니다.');
        } catch {
            toast.error('상태 변경 실패');
        }
    };

    const columns: Column<Executive>[] = [
        {
            key: 'photo_url' as keyof Executive,
            header: '사진',
            width: '60px',
            align: 'center',
            render: (v: string, row) => v ? (
                <img src={v} alt={row.name} className="w-9 h-9 rounded-full object-cover" />
            ) : <span className="w-9 h-9 inline-block rounded-full bg-neutral-100" />,
        },
        { key: 'role', header: '직책', width: '100px', render: (v) => <span>{v}</span> },
        { key: 'name', header: '이름', render: (v) => <span className="font-medium">{v}</span> },
        {
            key: 'is_active',
            header: '노출',
            width: '70px',
            align: 'center',
            render: (value, row) => (
                <Switch
                    checked={!!value}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggle(row)}
                    aria-label="노출 여부"
                />
            ),
        },
    ];

    return (
        <div>
            <Section
                className="overflow-hidden"
                title={
                    <>
                        경영진
                        {!loading && (
                            <span className="text-xs font-normal text-muted-foreground tabular-nums">
                                총 <span className="font-medium text-foreground">{list.length}</span>건
                            </span>
                        )}
                    </>
                }
                description="홈페이지 회사소개 > 경영진소개에 노출될 임원을 관리합니다. 행을 클릭하면 수정할 수 있습니다."
                actions={<Button variant="primary" icon={<PlusIcon />} onClick={openCreate}>경영진 추가</Button>}
                flush
            >
                {loading ? (
                    <Loading text="경영진 정보를 불러오는 중..." />
                ) : (
                    <Table
                        columns={columns}
                        data={list}
                        pagination
                        pageSize={20}
                        emptyMessage="등록된 경영진이 없습니다."
                        onRowClick={openEdit}
                        actionsColumnWidth="90px"
                        actions={(row) => (
                            <Button
                                size="small"
                                variant="danger-outline"
                                onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
                            >
                                삭제
                            </Button>
                        )}
                    />
                )}
            </Section>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editing ? '경영진 수정' : '경영진 추가'}
                size="large"
            >
                <div className="grid grid-cols-2 gap-3">
                    <div className="form-group">
                        <label>이름 *</label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="이광흠" />
                    </div>
                    <div className="form-group">
                        <label>직책 *</label>
                        <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="대표이사" />
                    </div>
                </div>

                <div className="form-group">
                    <label>프로필 사진</label>
                    <ImageUploader
                        images={photoImages}
                        onChange={setPhotoImages}
                        maxImages={1}
                        multiple={false}
                        accept="image/*"
                    />
                    <p className="text-xs text-neutral-400 mt-1">JPG/PNG/WEBP 등 이미지 파일. 1장만 업로드.</p>
                </div>

                <div className="form-group">
                    <label>경영 철학 (인용문)</label>
                    <Input value={form.philosophy} onChange={(e) => setForm({ ...form, philosophy: e.target.value })} placeholder="안전을 통한 가치창출..." />
                </div>

                <StringList
                    label="자격"
                    list={form.qualifications}
                    onChange={(v) => setForm({ ...form, qualifications: v })}
                    placeholder="예: 건축전기설비기술사"
                />

                <StringList
                    label="학력"
                    list={form.education}
                    onChange={(v) => setForm({ ...form, education: v })}
                    placeholder="예: 명지대학교 공과대학 전기공학과 졸업"
                />

                <PairList<CareerItem>
                    label="경력"
                    list={form.career}
                    descKey="desc"
                    onChange={(v) => setForm({ ...form, career: v })}
                    yearLabel="2006 ~ 현재"
                    descLabel="유원이앤에프(주) 대표이사"
                />

                <PairList<AwardItem>
                    label="수상"
                    list={form.awards}
                    descKey="label"
                    onChange={(v) => setForm({ ...form, awards: v })}
                    yearLabel="2016"
                    descLabel="대통령 표창"
                />

                <StringList
                    label="학회 활동"
                    list={form.societies}
                    onChange={(v) => setForm({ ...form, societies: v })}
                    placeholder="예: 한국기술사회 평생회원"
                />

                <StringList
                    label="저서"
                    list={form.publications}
                    onChange={(v) => setForm({ ...form, publications: v })}
                    placeholder="예: 핵심 소방기술사 (엔트미디어, 2004)"
                />

                <div className="modal-footer">
                    <Button variant="primary" onClick={handleSave} loading={uploading} disabled={!form.name || !form.role}>
                        {editing ? '수정' : '추가'}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={uploading}>취소</Button>
                </div>
            </Modal>
        </div>
    );
};

export default ExecutiveManager;
